import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";

// ✅ LOCAL IMAGES
import slide1 from "../assets/login/slide1.jpg";
import slide2 from "../assets/login/slide2.jpg";
import slide3 from "../assets/login/slide3.jpg";

import API from "../api";

export default function Login() {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    email: "",
    password: "",
    remember: false
  });

  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [slide1, slide2, slide3];

  /* ===============================
     AUTO SLIDE
  =============================== */
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  /* ===============================
     AUTO REDIRECT IF LOGGED IN
  =============================== */
  useEffect(() => {
    const token =
      localStorage.getItem("token") ||
      sessionStorage.getItem("token");

    if (token) {
      console.log("🔁 Already logged in, redirecting...");
      window.location.href = "/#/rcreate/dashboard";
    }
  }, [navigate]);

  /* ===============================
     HANDLE INPUT
  =============================== */
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setForm({
      ...form,
      [name]: type === "checkbox" ? checked : value
    });
  };

  /* ===============================
     LOGIN FUNCTION (FINAL FIXED)
  =============================== */
  const handleLogin = async (e) => {
    if (e) e.preventDefault();

    console.log("🔥 LOGIN BUTTON CLICKED");

    if (!form.email || !form.password) {
      setError("⚠️ Please fill all fields");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const url = `${API}/auth/login`;
      console.log("📡 Sending request to:", url);

      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          email: form.email.trim(),
          password: form.password
        })
      });

      console.log("📡 Status:", res.status);

      let data;
      try {
        data = await res.json();
        console.log("📦 Data:", data);
      } catch (err) {
        console.error("❌ JSON parse error:", err);
        throw new Error("Invalid server response");
      }

      if (!res.ok) {
        if (res.status === 401) {
          setError("❌ Invalid email or password");
        } else if (res.status === 403) {
          setError("❌ Access forbidden (CORS or backend issue)");
        } else {
          setError(`❌ ${data.message || "Login failed"}`);
        }
        setLoading(false);
        return;
      }

      if (data.success && data.token) {
        console.log("✅ LOGIN SUCCESS");

        // CLEAR OLD
        localStorage.removeItem("token");
        sessionStorage.removeItem("token");

        // SAVE TOKEN
        localStorage.setItem("token", data.token);

        // SAVE USER
        if (data.user) {
          localStorage.setItem("user", JSON.stringify(data.user));
        }

        // TRIGGER STATE UPDATE
        window.dispatchEvent(new Event("storage"));

        // REDIRECT
        setTimeout(() => {
          navigate("/rcreate/dashboard", { replace: true });
        }, 200);

      } else {
        setError(`❌ ${data.message || "Invalid email or password"}`);
      }

    } catch (err) {
      console.error("❌ LOGIN ERROR:", err);

      if (err.message.includes("fetch")) {
        setError("❌ Cannot connect to server");
      } else {
        setError("❌ Server error. Please try again.");
      }
    }

    setLoading(false);
  };

  /* ===============================
     ENTER KEY SUPPORT
  =============================== */
  const handleKeyPress = (e) => {
    if (e.key === "Enter") handleLogin(e);
  };

  return (
    <div className="min-h-screen flex">

      {/* ===============================
         LEFT SIDE (CAROUSEL)
      =============================== */}
      <div className="hidden md:flex w-1/2 relative overflow-hidden">

        {slides.map((img, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              currentSlide === index ? "opacity-100" : "opacity-0"
            }`}
          >
            <img
              src={img}
              alt="slide"
              className="w-full h-full object-cover"
            />
          </div>
        ))}

        {/* Overlay */}
        <div className="absolute inset-0 bg-black/60 flex items-center justify-center text-white p-10">
          <div className="text-center max-w-md">
            <h1 className="text-4xl font-bold mb-4">
              RCreate Virtual Assistant Services
            </h1>

            <p className="text-lg opacity-90 mb-4">
              Scale your business with professional virtual assistants
            </p>

            <p className="text-sm opacity-80">
              Operations, marketing, and support — all in one platform.
            </p>
          </div>
        </div>

        {/* Dots */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
          {slides.map((_, i) => (
            <div
              key={i}
              className={`w-3 h-3 rounded-full ${
                currentSlide === i ? "bg-white" : "bg-white/40"
              }`}
            />
          ))}
        </div>

      </div>

      {/* ===============================
         RIGHT SIDE (LOGIN)
      =============================== */}
      <div className="w-full md:w-1/2 flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 px-4">

        <div className="w-full max-w-md">

          {/* ✅ FORM WRAPPER (FIXED) */}
          <form
            onSubmit={handleLogin}
            className="bg-white/80 backdrop-blur-xl p-8 rounded-2xl shadow-xl border"
          >

            {/* HEADER */}
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold">Welcome Back 👋</h2>
              <p className="text-gray-500 text-sm">
                RCreate Admin Portal
              </p>
            </div>

            {/* ERROR */}
            {error && (
              <div className="bg-red-100 text-red-600 p-3 rounded mb-4 text-sm">
                {error}
              </div>
            )}

            {/* EMAIL */}
            <div className="relative mb-4">
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                onKeyDown={handleKeyPress}
                placeholder=" "
                className="peer w-full p-3 border rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
              />
              <label className="absolute left-3 top-2 text-gray-500 text-sm transition-all
                peer-placeholder-shown:top-3 peer-placeholder-shown:text-base
                peer-focus:top-1 peer-focus:text-xs">
                Email Address
              </label>
            </div>

            {/* PASSWORD */}
            <div className="relative mb-4">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                value={form.password}
                onChange={handleChange}
                onKeyDown={handleKeyPress}
                placeholder=" "
                className="peer w-full p-3 border rounded-lg pr-10 focus:ring-2 focus:ring-green-500 outline-none"
              />
              <label className="absolute left-3 top-2 text-gray-500 text-sm transition-all
                peer-placeholder-shown:top-3 peer-placeholder-shown:text-base
                peer-focus:top-1 peer-focus:text-xs">
                Password
              </label>

              <span
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-3 cursor-pointer text-gray-500"
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </span>
            </div>

            {/* REMEMBER */}
            <div className="flex justify-between items-center mb-4 text-sm">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name="remember"
                  checked={form.remember}
                  onChange={handleChange}
                />
                Remember me
              </label>

              <span className="text-green-600 cursor-pointer hover:underline">
                Forgot password?
              </span>
            </div>

            {/* BUTTON */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-lg text-white font-semibold bg-gradient-to-r from-green-600 to-green-500 hover:opacity-90 transition shadow-lg disabled:opacity-50"
            >
              {loading ? "Logging in..." : "Login"}
            </button>

            {/* FOOTER */}
            <p className="text-center text-xs text-gray-400 mt-6">
              © {new Date().getFullYear()} RCreate Virtual Assistant Services
            </p>

          </form>

        </div>

      </div>

    </div>
  );
}